import PriceRowCommission from 'components/Luxkit/PricePoints/PriceRowCommission'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import React, { useMemo } from 'react'

interface Props {
  productType: App.ProductType;
  offerId?: string;
  offerLocations?: Array<string>;
  vendorId?: string;
}

type PriceRowCommissionProps = React.ComponentProps<typeof PriceRowCommission>

interface OfferCommissionParams {
  productType: App.ProductType;
  agent: string;
  agency: string;
  offer: string;
  location: Array<string>;
  vendor: string;
}

const productTypeToProductMap = new Map<App.ProductType, PriceRowCommissionProps['productType']>(
  [
    ['tactical_ao_hotel', 'hotel'],
    ['last_minute_hotel', 'hotel'],
    ['hotel', 'hotel'],
    ['bedbank_hotel', 'hotel'],
    ['ultralux_hotel', 'hotel'],
    ['rental', 'hotel'],
    ['bundle_and_save', 'hotel'],
    ['tour', 'tour'],
    ['direct_tour', 'tour'],
    ['partner_tour', 'tour'],
    ['ultra_lux_tour', 'tour'],
    ['connection_tour', 'tour'],
    ['cruise-flash', 'tour'],
    ['cruise', 'cruise'],
    ['cruise-exclusive', 'cruise'],
    ['cruise-tactical', 'cruise'],
    ['experience', 'experience'],
    ['transfer', 'experience'],
    ['always_on', 'experience'],
  ],
)

function checkIfOfferMatchCommissionConditions(commissionRule: App.AgentHubCommissionRule, offerData: OfferCommissionParams) {
  if (Object.keys(commissionRule.conditions).length === 0) {
    return false
  }

  const matchAllConditions = Object.keys(commissionRule.conditions)
    .every(condition => {
      if (condition === 'location') {
        return offerData.location?.some(location => commissionRule.conditions[condition]?.includes(location))
      }

      return commissionRule.conditions[condition]?.includes(offerData[condition])
    })

  return matchAllConditions
}

function useAgentHubCommission(props: Props): { commissionAmount: number } {
  const { productType, offerId, offerLocations, vendorId } = props
  const commissionAmountV1 = useAppSelector(state => state.agentHub.agentCommissions.commissionAmounts[productType])
  const product = productTypeToProductMap.get(productType)
  const baseRules = useAppSelector(state => state.agentHub.agentCommissionsV2.baseCommissions)

  const additionalRules = useAppSelector(state => state.agentHub.agentCommissionsV2.additionalCommissions)

  const agentAccount = useAppSelector(state => state.agentHub.agentHubAccount.account)

  const offerCommissionParams: OfferCommissionParams = useMemo(() => ({
    productType: product || '' as App.ProductType,
    agent: agentAccount?.agentId || '',
    agency: agentAccount?.agencyId || '',
    offer: offerId || '',
    location: offerLocations?.length ? offerLocations : [''],
    vendor: vendorId || '',
  }), [agentAccount, offerId, offerLocations, product, vendorId])

  const commissionAmount = useMemo(() => {
    let baseCommission = 0
    let additionalCommission = 0

    // Find the highest base commission that matches all conditions
    baseRules.forEach(commissionRule => {
      if (commissionRule.commissionPercentage < baseCommission) {
        return
      }

      const offerMatchAllConditions = checkIfOfferMatchCommissionConditions(commissionRule, offerCommissionParams)

      if (offerMatchAllConditions) {
        baseCommission = commissionRule.commissionPercentage
      }
    })

    // then add any additional commission that matches all conditions
    additionalRules.forEach(commissionRule => {
      const matchAllConditions = checkIfOfferMatchCommissionConditions(commissionRule, offerCommissionParams)

      if (matchAllConditions) {
        additionalCommission += commissionRule.commissionPercentage
      }
    })

    return baseCommission + additionalCommission
  }, [additionalRules, baseRules, offerCommissionParams])

  return {
    commissionAmount: config.AGENT_COMMISSIONS_V2_ENABLED ? commissionAmount : (commissionAmountV1 || 0),
  }
}

export default useAgentHubCommission
